import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import { toast } from '@/ui/toast';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { AiSelectedAnalysisProgressEventEntity } from '@/entities/ai-selected-analysis-progress-event.entity';
import { Sort } from '@/types/sort.type';
import { AiSelectedAnalysisProgress } from '@/context/reviews-page.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useRefreshClientAiQuota } from '@/hooks/use-refresh-client-ai-quota';
import {
    getAiSelectedAnalysisProgressMessages,
    resolveAiSelectedAnalysisCompletedMessage,
    resolveAiSelectedAnalysisTerminalMessage,
} from '@/shared/ai-selected-analysis-progress-messages';
import { isOfferNotAcceptedMarketplaceErrorDescriptor } from '@/shared/errors/marketplace-api-error-message';
import { createAiAnalysisToastId } from '@/ui/toast-ids';

type UseAiAnalysisFinalizationParams = {
    accessToken: string;
    filtersQuery: string[];
    sort: Sort;
    selectionSessionId: string;
    setPickedReviews?: (val: string[]) => void;
    setAiAnalysisProgress: Dispatch<SetStateAction<AiSelectedAnalysisProgress | null>>;
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    refetchReviewsAndProducts: () => Promise<void>;
    aiAnalysisOperationIdRef: MutableRefObject<string | null>;
    aiAnalysisFinalizingOperationIdRef: MutableRefObject<string | null>;
    applyAiSelectedAnalysisProgress: (event: AiSelectedAnalysisProgressEventEntity) => void;
    onAiAnalysisFinalized?: (event: AiSelectedAnalysisProgressEventEntity) => void;
};

type UseAiAnalysisFinalizationReturn = {
    finishAiSelectedAnalysis: (event: AiSelectedAnalysisProgressEventEntity) => Promise<void>;
};

const aiAnalysisFinalizationLogger = createClientLogger({
    scope: 'ai-analysis-finalization',
});
const aiSelectedAnalysisMessages = getAiSelectedAnalysisProgressMessages();

const isOfferNotAcceptedTerminalEvent = (event: AiSelectedAnalysisProgressEventEntity): boolean =>
    event.stage === 'failed' && isOfferNotAcceptedMarketplaceErrorDescriptor(event);

export const useAiAnalysisFinalization = ({
    accessToken,
    filtersQuery,
    sort,
    selectionSessionId,
    setPickedReviews,
    setAiAnalysisProgress,
    enqueueReviewSelectionMutation,
    refetchReviewsAndProducts,
    aiAnalysisOperationIdRef,
    aiAnalysisFinalizingOperationIdRef,
    applyAiSelectedAnalysisProgress,
    onAiAnalysisFinalized,
}: UseAiAnalysisFinalizationParams): UseAiAnalysisFinalizationReturn => {
    const { reviewsApi } = useMarketplaceApi();
    const refreshClientAiQuota = useRefreshClientAiQuota();

    const clearTerminalOperationState = useCallback(
        (operationId: string) => {
            const shouldClearOperationState = aiAnalysisOperationIdRef.current === operationId;
            setAiAnalysisProgress((current) => {
                if (!current || !shouldClearOperationState) {
                    return current;
                }

                return null;
            });

            if (shouldClearOperationState) {
                aiAnalysisOperationIdRef.current = null;
            }
            if (aiAnalysisFinalizingOperationIdRef.current === operationId) {
                aiAnalysisFinalizingOperationIdRef.current = null;
            }
        },
        [aiAnalysisFinalizingOperationIdRef, aiAnalysisOperationIdRef, setAiAnalysisProgress],
    );

    const reportFailedTerminalEvent = useCallback((event: AiSelectedAnalysisProgressEventEntity) => {
        logOfferNotAcceptedMarketplacePolicyWarning({
            logger: aiAnalysisFinalizationLogger,
            message:
                '[client.ai-analysis.offer-policy] Selected reviews AI analysis terminal event blocked before offer acceptance.',
            error: event,
            context: {
                action: 'ai_analysis.progress.terminal',
                operationId: event.operationId,
                clientId: event.clientId,
            },
        });
        toast.error(resolveAiSelectedAnalysisTerminalMessage({ event }), {
            toastId: createAiAnalysisToastId({
                operationId: event.operationId,
                outcome: `terminal:${event.stage}`,
            }),
        });
    }, []);

    const finishAiSelectedAnalysis = useCallback(
        async (event: AiSelectedAnalysisProgressEventEntity) => {
            if (aiAnalysisFinalizingOperationIdRef.current === event.operationId) {
                return;
            }

            aiAnalysisFinalizingOperationIdRef.current = event.operationId;
            applyAiSelectedAnalysisProgress(event);
            if (isOfferNotAcceptedTerminalEvent(event)) {
                clearTerminalOperationState(event.operationId);
                reportFailedTerminalEvent(event);
                return;
            }

            let finalizationSucceeded = true;

            try {
                await refetchReviewsAndProducts();
                await refreshClientAiQuota();

                if (selectionSessionId && setPickedReviews) {
                    const reconcileResponse = await enqueueReviewSelectionMutation(() =>
                        reviewsApi.reconcileReviewSelectionSession(
                            {
                                sessionId: selectionSessionId,
                                search: filtersQuery,
                                ...sort,
                            },
                            { authToken: accessToken },
                        ),
                    );

                    setPickedReviews(reconcileResponse.data.ids);
                }
            } catch (error) {
                finalizationSucceeded = false;
                if (!isRequestCanceled(error)) {
                    logClientError({
                        logger: aiAnalysisFinalizationLogger,
                        message:
                            '[client.ai-analysis.selection-finalize] Failed to finalize selected reviews AI analysis.',
                        error,
                        context: {
                            operationId: event.operationId,
                            stage: event.stage,
                        },
                    });
                }
            } finally {
                clearTerminalOperationState(event.operationId);
            }

            if (event.stage === 'failed') {
                reportFailedTerminalEvent(event);
                return;
            }

            const terminalToastId = createAiAnalysisToastId({
                operationId: event.operationId,
                outcome: `terminal:${event.stage}`,
            });

            if (!finalizationSucceeded) {
                toast.warning(aiSelectedAnalysisMessages.finalizationRefreshFailed, {
                    toastId: terminalToastId,
                });
                return;
            }

            onAiAnalysisFinalized?.(event);

            if (event.stage === 'canceled') {
                toast.warning(aiSelectedAnalysisMessages.canceled, {
                    autoClose: false,
                    toastId: terminalToastId,
                });
                return;
            }

            if (!event.successCount) {
                toast.error(resolveAiSelectedAnalysisCompletedMessage({ event }), {
                    toastId: terminalToastId,
                });
                return;
            }

            if (event.failedCount) {
                toast.warning(resolveAiSelectedAnalysisCompletedMessage({ event }), {
                    toastId: terminalToastId,
                });
                return;
            }

            toast.success(resolveAiSelectedAnalysisCompletedMessage({ event }), {
                toastId: terminalToastId,
            });
        },
        [
            accessToken,
            aiAnalysisFinalizingOperationIdRef,
            applyAiSelectedAnalysisProgress,
            clearTerminalOperationState,
            enqueueReviewSelectionMutation,
            filtersQuery,
            refetchReviewsAndProducts,
            reportFailedTerminalEvent,
            reviewsApi,
            refreshClientAiQuota,
            selectionSessionId,
            setPickedReviews,
            sort,
            onAiAnalysisFinalized,
        ],
    );

    return {
        finishAiSelectedAnalysis,
    };
};
